import { GetMcaEstimatedScheduleResponse } from '@iwoca/lapi-client/edge';
import { Pill } from '@iwoca/orion';
import cn from 'classnames';
import { FormattedMessage } from 'react-intl';

import { getOriginalPricing } from './getOriginalPricing';
import styles from './RBLLoanSummary.module.css';
import { MoneyNoDecimals } from '../../../../../ui/moneyNoDecimals';
import { MoneyWithDecimals } from '../../../../../ui/moneyWithDecimals';
import { Rate } from '../../../../../ui/rate';

type WithdrawalSummaryProps = {
    schedule: GetMcaEstimatedScheduleResponse;
    loading: boolean;
    drawdownAmount: number;
};

export const WithdrawalSummary = ({
    schedule,
    loading,
    drawdownAmount
}: WithdrawalSummaryProps) => {
    const summary = schedule?.data.summary;

    const originalFeeAmount = getOriginalPricing({
        numberOfRepayments: schedule?.data.estimated_schedule.repayments.length,
        totalCreditAmount: summary.total_credit_amount,
        totalPayable: summary.total_payable
    });

    // only highlight upfront fee savings > 2.5%, i.e. where the new fee is 97.5% or below the current fee.
    const hasDiscountedPricingDE =
        summary.funding_fee <= 0.975 * originalFeeAmount;

    return (
        <div className={styles.loanSummary}>
            <div className={styles.summaryRow}>
                <FormattedMessage
                    id="_.rbl_loan_summary.withdrawal_amount_confirmation"
                    defaultMessage="You're withdrawing:"
                />
                <MoneyNoDecimals
                    className={cn(styles.subText, {
                        [styles.loading]: loading
                    })}
                    value={drawdownAmount}
                />
            </div>

            {hasDiscountedPricingDE && (
                <div className={styles.discountPill}>
                    <Pill>
                        <FormattedMessage
                            id="_.rbl_loan_summary.discountedOffer"
                            defaultMessage="Our offer for you: You save {amount}"
                            values={{
                                amount: (
                                    <span>
                                        &thinsp;
                                        <MoneyWithDecimals
                                            className={cn({
                                                [styles.loading]: loading
                                            })}
                                            value={
                                                originalFeeAmount -
                                                summary.funding_fee
                                            }
                                        />
                                    </span>
                                )
                            }}
                        />
                    </Pill>
                </div>
            )}

            <div className={styles.summaryRow}>
                <div className={styles.feeColumn}>
                    <div>
                        <FormattedMessage
                            id="_.rbl_loan_summary.fee_amount"
                            defaultMessage="Your fixed fee is:"
                        />
                    </div>
                    <div className={styles.feeNoInterestCopy}>
                        <FormattedMessage
                            id="_.rbl_loan_summary.no_interest"
                            defaultMessage="(No interest at all)"
                        />
                    </div>
                </div>

                <div className={styles.feeAmountColumn}>
                    {hasDiscountedPricingDE && (
                        <MoneyWithDecimals
                            className={cn(
                                styles.originalAmountWithStrikethrough,
                                styles.subText,
                                {
                                    [styles.loading]: loading
                                }
                            )}
                            value={originalFeeAmount}
                        />
                    )}
                    <MoneyWithDecimals
                        className={cn(styles.subText, {
                            [styles.loading]: loading
                        })}
                        value={summary.funding_fee}
                    />

                    <div
                        className={cn(styles.feePercentage, {
                            [styles.loading]: loading
                        })}
                    >
                        <Rate
                            className={styles.feePercentageAmount}
                            value={
                                summary.funding_fee /
                                summary.total_credit_amount
                            }
                        />{' '}
                        <FormattedMessage
                            id="_.rbl_loan_summary.fee_proportion"
                            defaultMessage="of your withdrawal"
                        />
                    </div>
                </div>
            </div>

            <div className={styles.summaryRow}>
                <FormattedMessage
                    id="_.rbl_loan_summary.total_repayment"
                    defaultMessage="Your total repayment is:"
                />

                <MoneyNoDecimals
                    className={cn(styles.subText, {
                        [styles.loading]: loading
                    })}
                    value={summary.total_payable}
                />
            </div>
        </div>
    );
};
